import React, { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const { login, user } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [busy, setBusy] = useState(false);

  if (user) {
    const from = (location.state as any)?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, motDePasse);
      toast.success('Connexion réussie');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Échec de la connexion');
    } finally {
      setBusy(false);
    }
  };

  const loginAsDemo = async () => {
    setBusy(true);
    try {
      await login('demo@tunifact.tn', 'demo1234');
    } catch (err: any) {
      toast.error('Le compte démo n\'est pas disponible sur cette instance.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-green-50 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg border border-emerald-100 p-8">
          <div className="flex items-center gap-3 mb-8">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3c.5.12 1 .2 1.5.2C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" />
              </svg>
            </span>
            <div>
              <span className="text-2xl font-extrabold tracking-tight text-slate-900">GreenBill</span>
              <div className="text-xs text-emerald-600 font-medium">Facturation éco-responsable</div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Bienvenue</h1>
          <p className="text-sm text-slate-500 mb-6">Connectez-vous à votre espace de facturation.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                     placeholder="vous@entreprise.tn"
                     className="w-full rounded-lg border border-slate-200 px-3 py-2.5 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe</label>
              <input type="password" value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} required autoComplete="current-password"
                     placeholder="••••••••"
                     className="w-full rounded-lg border border-slate-200 px-3 py-2.5 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition" />
            </div>
            <button type="submit" disabled={busy}
                    className="w-full rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold py-2.5 hover:from-emerald-600 hover:to-green-700 disabled:opacity-50 transition shadow-sm">
              {busy ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-slate-400">ou</span></div>
          </div>
          <button type="button" onClick={loginAsDemo} disabled={busy}
                  className="w-full rounded-lg border border-emerald-200 text-emerald-700 font-semibold py-2.5 hover:bg-emerald-50 disabled:opacity-50 transition">
            Essayer avec le compte démo
          </button>
          <p className="text-sm text-slate-500 mt-5 text-center">
            <Link to="/forgot-password" className="text-emerald-600 font-semibold hover:text-emerald-700">Mot de passe oublié&nbsp;?</Link>
          </p>
          <p className="text-sm text-slate-500 mt-2 text-center">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-emerald-600 font-semibold hover:text-emerald-700">Créer un compte</Link>
          </p>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} GreenBill — Tous droits réservés
        </p>
      </div>
    </div>
  );
};

export default Login;
